import React, { useState, useEffect } from "react";
import { History, RotateCcw } from "lucide-react";
import { format } from "date-fns";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * VersionsDrawer
 *
 * Pops open a dialog listing all historical versions of the report with revert buttons.
 *
 * Props:
 *  - reportId: string
 *  - editedAt: ISO timestamp (last edit) — shown in the trigger button
 *  - onReverted: () => Promise<void> — caller refreshes the report after a revert
 *  - scope: 'doctor' | 'patient' | 'both' — which part of the report a revert touches
 */
export default function VersionsDrawer({ reportId, editedAt, onReverted, scope = "both" }) {
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reverting, setReverting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/${reportId}/versions`);
      setVersions(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const revert = async (versionId) => {
    const scopeLabel =
      scope === "doctor" ? "the clinical summary only"
      : scope === "patient" ? "the patient narrative + clinician note only"
      : "both narratives";
    if (!window.confirm(`Restore ${scopeLabel} from this version? Current content will be saved as a new version first.`)) return;
    setReverting(versionId);
    try {
      await api.post(`/reports/${reportId}/revert/${versionId}`, { scope });
      toast.success("Reverted");
      setOpen(false);
      await onReverted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revert");
    } finally {
      setReverting(null);
    }
  };

  if (!editedAt) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="report-versions-button"
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs font-medium hover:bg-white"
          style={{ borderColor: "var(--color-border)", color: "var(--color-text-secondary)" }}
          title="Edit history"
        >
          <History className="w-3.5 h-3.5" />
          Edited {format(new Date(editedAt), "d MMM HH:mm")}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Edit history</DialogTitle>
        </DialogHeader>
        <p className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
          Restoring from this tab affects:{" "}
          <span className="font-medium" style={{ color: "var(--color-text-primary)" }}>
            {scope === "doctor" ? "Clinical summary only" : scope === "patient" ? "Patient narrative + clinician note (current language only)" : "Both narratives"}
          </span>
        </p>
        {loading ? (
          <div className="p-6 text-center"><div className="spinner mx-auto" /></div>
        ) : versions.length === 0 ? (
          <div className="p-6 text-center text-sm" style={{ color: "var(--color-text-secondary)" }}>
            No edits yet.
          </div>
        ) : (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {versions.map((v) => {
              const previewText = scope === "doctor" ? v.previous_doctor : v.previous_patient;
              return (
                <div key={v.version_id} data-testid={`version-row-${v.version_id}`} className="border rounded-lg p-4" style={{ borderColor: "var(--color-border)" }}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>
                        {format(new Date(v.edited_at), "d MMM yyyy · HH:mm")} · {v.language?.toUpperCase()}
                        {v.revert_scope && <span className="ml-2 px-1.5 py-0.5 rounded" style={{ background: "var(--bg-canvas)" }}>revert</span>}
                      </div>
                      <div className="font-medium mt-1 truncate" style={{ color: "var(--color-text-primary)" }}>
                        {v.edited_by_name || "Clinician"}
                      </div>
                      {v.note && (
                        <div className="text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>"{v.note}"</div>
                      )}
                      <details className="mt-3 text-xs">
                        <summary className="cursor-pointer" style={{ color: "var(--color-primary)" }}>
                          Preview previous {scope === "doctor" ? "clinical summary" : "patient narrative"}
                        </summary>
                        <div className="mt-2 p-3 rounded-md border whitespace-pre-wrap max-h-48 overflow-y-auto font-mono text-[11px] leading-relaxed" style={{ borderColor: "var(--color-border)", background: "var(--bg-canvas)" }}>
                          {(previewText || "").slice(0, 800) || <em style={{ color: "var(--color-text-secondary)" }}>(empty)</em>}
                          {(previewText || "").length > 800 ? "…" : ""}
                        </div>
                      </details>
                    </div>
                    <button
                      data-testid={`version-revert-${v.version_id}`}
                      onClick={() => revert(v.version_id)}
                      disabled={reverting === v.version_id || !previewText}
                      title={!previewText ? "Nothing to restore for this scope" : ""}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium hover:bg-white disabled:opacity-40 flex-shrink-0"
                      style={{ borderColor: "var(--color-border)" }}
                    >
                      <RotateCcw className="w-3.5 h-3.5" /> Restore
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
